import React, { Suspense, useContext, useState, useEffect, useMemo } from 'react';
import { useDashboardTranslation } from './i18n';
import { createPortal } from 'react-dom';
import { GripVertical, Maximize2, Minimize2, X, RefreshCw, AlertTriangle, Users } from 'lucide-react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import type { BaseWidgetProps } from '../components/DashboardEngine/types';
import type { DashboardPeriod } from './types';
import { WidgetErrorBoundary } from '../components/DashboardEngine/components/WidgetErrorBoundary';
import { WidgetSkeleton as ShapedSkeleton } from './components/WidgetSkeletons';
import { skeletonShapeFor } from './utils/skeletonShape';
import { useReducedMotion } from './utils/useReducedMotion';
import { DASHBOARD_LOCALE } from './utils/formatNumber';
import { widgetDataQueryKey } from './api/widgetEnvelope';
import { readPositiveNumber } from './utils/widgetConfig';
import { DashboardContext } from './useDashboardContext';
import { WidgetAccessDialog } from './access/WidgetAccessDialog';

interface WrapperProps extends BaseWidgetProps {
  children: React.ReactNode;
  onRemove?: (id: string) => void;
}

/**
 * Esqueleto genérico, conservado por compatibilidad.
 * @deprecated Usar `WidgetSkeleton` de `./components/WidgetSkeletons`, que respeta
 * la forma del widget. Este cae en la forma `generic`.
 */
export const WidgetSkeleton = () => <ShapedSkeleton shape="generic" />;

/**
 * Antigüedad del dato, en lenguaje relativo.
 *
 * Las claves llevan la plantilla completa (`hace {n} min` / `{n} min ago`) y no el
 * adverbio suelto: en español el "hace" va delante y en inglés el "ago" detrás, así
 * que componer la frase por partes produce " 2 min" en uno de los dos idiomas.
 */
function formatTimestamp(ms: number, t: (key: string) => string): string {
  if (!ms) return '';
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60) return t('dashboard.widget.justNow');
  if (diff < 3600) return t('dashboard.widget.agoMinutes').replace('{n}', String(Math.floor(diff / 60)));
  if (diff < 86400) return t('dashboard.widget.agoHours').replace('{n}', String(Math.floor(diff / 3600)));
  return new Date(ms).toLocaleString(DASHBOARD_LOCALE, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

export const WidgetWrapper: React.FC<WrapperProps> = ({
  preference,
  definition,
  isEditMode,
  globalFilters,
  onRemove,
  children
}) => {
  const { t } = useDashboardTranslation();
  const [isMaximized, setIsMaximized] = useState(false);
  const reducedMotion = useReducedMotion();

  // ── Refresh manual: observar la query del widget via cache ──
  const queryClient = useQueryClient();
  // Por el constructor compartido y no a mano: si la forma de la clave cambia, este observador
  // la sigue. Escrita a mano, dejaría de encontrar la entrada sin que nada avisara.
  // Memorizada para poder declararla en las deps del efecto sin que este se re-ejecute en cada
  // render: sus entradas son exactamente las que ya estaban en el array de dependencias.
  const period = globalFilters?.period as DashboardPeriod | undefined;
  const queryKey = useMemo(
    () => widgetDataQueryKey(definition.widget_id, period as DashboardPeriod),
    [definition.widget_id, period],
  );

  // Reactivo: true si la query con este key está en curso
  const isRefetching = useIsFetching({ queryKey }) > 0;

  // Reactivo: timestamp + data length via suscripción al cache
  const [dataUpdatedAt, setDataUpdatedAt] = useState(0);
  const [dataLength, setDataLength] = useState(0);
  const [, forceRender] = useState(0);
  useEffect(() => {
    let isMounted = true;

    const readState = () => {
      if (!isMounted) return;
      const state = queryClient.getQueryState(queryKey);
      const cachedResult = queryClient.getQueryData<{
        data: unknown;
        schema: unknown;
        meta: { generatedAt?: string; rowCount?: number } | null;
      }>(queryKey);

      // Timestamp: preferir meta.generatedAt del envelope, fallback a React Query timestamp
      const generatedAt = cachedResult?.meta?.generatedAt
        ? new Date(cachedResult.meta.generatedAt).getTime()
        : null;
      const ts = generatedAt ?? (state?.dataUpdatedAt ?? 0);
      setDataUpdatedAt(prev => ts !== prev ? ts : prev);

      // Volumen: preferir rowCount del envelope (autoritativo), fallback a length del array
      const rowCount = cachedResult?.meta?.rowCount
        ?? (Array.isArray(cachedResult?.data) ? (cachedResult.data as unknown[]).length : 0);
      setDataLength(rowCount);
    };

    readState();
    const unsubscribe = queryClient.getQueryCache().subscribe(readState);
    const tick = setInterval(() => { if (isMounted) forceRender(n => n + 1); }, 30_000);

    return () => {
      isMounted = false;
      unsubscribe();
      clearInterval(tick);
    };
  }, [queryClient, queryKey]);

  // Umbral de volumen configurable por widget via config_schema. Default: 5000 registros.
  // Validado: un `"5000"` en texto o un valor negativo caen al respaldo en vez de comparar basura.
  const criticalVolume = readPositiveNumber(
    (definition as unknown as Record<string, unknown>).config_schema,
    'criticalVolumeThreshold',
    5000,
  );
  const isHighVolume = dataLength > criticalVolume;

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    queryClient.refetchQueries({ queryKey });
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) onRemove(preference.id);
  };

  const title = (preference.config_json?.title as string) || definition.nombre;

  /**
   * Subtítulo: periodo y antigüedad del dato.
   *
   * El periodo baja aquí desde el cuerpo del widget, donde se repetía junto al
   * título — "Ventas del periodo" arriba y "Ventas del periodo (Semana)" abajo.
   * Dos líneas para decir lo mismo, robándole el espacio a la cifra.
   */
  const freshness = formatTimestamp(dataUpdatedAt, t);
  const subtitle = [period, freshness].filter(Boolean).join(' · ');

  /**
   * Los tres botones del header.
   *
   * <b>Ocultos hasta hover, foco o táctil</b>, con `opacity` y NO con `display`: si
   * salieran del flujo, el header cambiaría de alto al pasar el mouse y el contenido
   * saltaría. `group-focus-within` es lo que los revela a quien navega con teclado,
   * y `[@media(hover:none)]` lo que los deja fijos en tablet — donde no hay hover
   * que valga y esconderlos sería esconderlos para siempre.
   */
  const chromeButtonClass = 'p-1 rounded transition-colors hover:text-[var(--dash-accent)]';

  /**
   * 🔴 El botón de acceso, y las tres condiciones que lo hacen aparecer.
   *
   * Se pinta solo si (1) el host proveyó un adaptador —sin modelo de acceso por instancia no hay
   * nada que ofrecer—, (2) el SERVIDOR concedió el verbo correspondiente sobre ESTE widget, y (3) por
   * tanto nunca a partir de un `isAdmin` global. `can_share` abre la pestaña de personas y `can_edit`
   * la de política: con cualquiera de las dos el diálogo tiene contenido, sin ninguna sobra el botón.
   *
   * <para>Vive en el cromo de la tarjeta —oculto hasta hover, como sus vecinos— porque el sitio
   * natural para compartir algo es la cosa misma. La pantalla de revisión, transversal, es para lo
   * contrario: auditar lo repartido sin ir widget por widget.</para>
   */
  // 🔴 Se lee el contexto CRUDO y no con `useDashboardContext()`: ese hook LANZA cuando no hay
  // provider, y la tarjeta se monta sola —en los tests del contenedor y en un host que arme piezas
  // del tablero por separado, que el propio contexto declara soportado—. Un adaptador opcional no
  // puede convertir el provider en obligatorio.
  const accessAdapter = useContext(DashboardContext)?.accessAdapter;
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const puedeAbrirAcceso =
    !!accessAdapter &&
    (definition.permissions?.can_share === true || definition.permissions?.can_edit === true);

  const accessButton = puedeAbrirAcceso ? (
    <button
      onClick={(e) => { e.stopPropagation(); setIsAccessOpen(true); }}
      className={chromeButtonClass}
      style={{ color: 'var(--dash-text-muted)' }}
      title={t('dashboard.access.dialogTitle')}
      aria-label={t('dashboard.access.dialogTitle')}
      data-testid="widget-access-button"
    >
      <Users className="w-3.5 h-3.5" />
    </button>
  ) : null;

  const refreshButton = (
    <button
      onClick={handleRefresh}
      className={chromeButtonClass}
      style={{ color: 'var(--dash-text-muted)' }}
      title={t('dashboard.widget.refresh')}
      aria-label={t('dashboard.widget.refresh')}
    >
      <RefreshCw className={`w-3.5 h-3.5 ${isRefetching && !reducedMotion ? 'animate-spin' : ''}`} />
    </button>
  );

  const widgetContent = (
    <WidgetErrorBoundary widgetId={definition.widget_id}>
      <Suspense fallback={<ShapedSkeleton shape={skeletonShapeFor(definition.tipo)} reducedMotion={reducedMotion} />}>
        {children}
      </Suspense>
    </WidgetErrorBoundary>
  );

  /**
   * El aviso de alto volumen queda FUERA del bloque de cromo, a propósito: es una
   * advertencia, no una acción, y una advertencia que sólo aparece en hover no
   * advierte de nada.
   */
  const highVolumeBadge = isHighVolume ? (
    <span
      className="p-1 flex-shrink-0"
      style={{ color: 'var(--dash-danger)' }}
      title={t('dashboard.widget.highVolume').replace('{count}', dataLength.toLocaleString(DASHBOARD_LOCALE))}
      data-testid="widget-high-volume"
    >
      <AlertTriangle className="w-3.5 h-3.5" />
    </span>
  ) : null;

  return (
    <>
      <div
        className="group w-full h-full flex flex-col rounded-[12px] overflow-hidden transition-shadow duration-200 border"
        style={{
          background: 'var(--dash-surface-card)',
          borderColor: 'var(--dash-border)',
          // Sombra mínima en reposo: sin ella la tarjeta se lee como una celda de
          // tabla y no como un objeto. No hay `transform` en hover — una tarjeta
          // que se mueve al pasar el mouse desordena la lectura de la grilla.
          boxShadow: '0 1px 2px rgba(16,24,40,.04)',
        }}
        data-testid={`widget-card-${definition.widget_id}`}
      >
        {/* Cabecera */}
        <div
          className={`flex items-start justify-between gap-2 px-4 pt-3 pb-2 ${isEditMode ? 'cursor-move drag-handle' : ''}`}
        >
          <div className="flex items-start gap-2 overflow-hidden min-w-0">
            {isEditMode && (
              <GripVertical
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--dash-text-muted)' }}
              />
            )}
            <div className="min-w-0">
              {/* Un solo título. El icono de info que iba aquí se retiró: no era
                  accionable y competía con la etiqueta por el mismo renglón. */}
              {/* 15px y semibold: a 13px/medium el titulo se perdia contra la cifra,
                  que es lo unico que compite con el en la tarjeta. */}
              <h3
                className="text-[15px] font-semibold truncate leading-tight"
                style={{ color: 'var(--dash-text-primary)' }}
                title={title}
              >
                {title}
              </h3>
              {subtitle && (
                <p
                  className="text-[11px] truncate leading-tight mt-0.5"
                  style={{ color: 'var(--dash-text-muted)' }}
                  data-testid="widget-subtitle"
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-start gap-0.5 flex-shrink-0">
            {highVolumeBadge}
            <div
              className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-[120ms] ease-out"
              data-testid="widget-chrome"
            >
              {accessButton}
              {refreshButton}
              <button
                onClick={(e) => { e.stopPropagation(); setIsMaximized(true); }}
                className={chromeButtonClass}
                style={{ color: 'var(--dash-text-muted)' }}
                title={t('dashboard.widget.maximize')}
                aria-label={t('dashboard.widget.maximize')}
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleRemove}
                className={chromeButtonClass}
                style={{ color: 'var(--dash-text-muted)' }}
                title={t('dashboard.widget.remove')}
                aria-label={t('dashboard.widget.remove')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Cuerpo — oculto cuando está maximizado (el portal lo muestra en fullscreen) */}
        <div className={`flex-1 overflow-hidden relative transition-opacity duration-300 ${isRefetching ? 'opacity-60' : ''} ${isMaximized ? 'invisible' : ''}`}>
          {widgetContent}
        </div>
      </div>

      {/* Modal maximizado */}
      {isMaximized && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 md:p-8">
          <div
            className="w-full h-full max-w-7xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            style={{ background: 'var(--dash-surface-card)' }}
          >
            <div
              className="flex items-center justify-between px-5 py-3 border-b"
              style={{ borderColor: 'var(--dash-border)' }}
            >
              <div className="min-w-0">
                <h3 className="text-lg font-semibold truncate" style={{ color: 'var(--dash-text-primary)' }}>
                  {title}
                </h3>
                {subtitle && (
                  <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--dash-text-muted)' }}>
                    {subtitle}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {highVolumeBadge}
                {refreshButton}
                {/* Cerrar no está aquí a propósito: borrar desde el modal deja al
                    usuario mirando un widget que ya no existe. */}
                <button
                  onClick={(e) => { e.stopPropagation(); setIsMaximized(false); }}
                  className="p-1.5 rounded-md transition-colors hover:text-[var(--dash-accent)]"
                  style={{ color: 'var(--dash-text-secondary)' }}
                  title={t('dashboard.widget.minimize')}
                  aria-label={t('dashboard.widget.minimize')}
                >
                  <Minimize2 className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* `dash-expanded` sube la escala del contenido escalar y lo centra:
                ampliar cambia la DENSIDAD, no solo el tamaño del contenedor. */}
            <div className={`dash-expanded flex-1 overflow-auto relative p-4 transition-opacity duration-300 ${isRefetching ? 'opacity-60' : ''}`}>
              {widgetContent}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* El diálogo de acceso. Se monta solo cuando hace falta y el host proveyó el adaptador. */}
      {accessAdapter && isAccessOpen && (
        <WidgetAccessDialog
          widget={definition}
          adapter={accessAdapter}
          isOpen
          onClose={() => setIsAccessOpen(false)}
          // Cambiar el acceso puede sacar este widget del catálogo de quien lo cambió —restringirlo
          // sin darse un grant, o exigir una clase que no tiene— así que hay que refrescar lo que la
          // pantalla cree saber. `refetchQueries` sin clave recarga el catálogo y los datos.
          onChanged={() => { void queryClient.refetchQueries(); }}
        />
      )}
    </>
  );
};
